import { supabase } from '../supabase/client';
import { NotificationService } from './notificationService';
import { MixpanelAnalytics } from '../../analytics';

// 檢查是否為有效的 UUID
const isValidUUID = (str: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

export interface Message {
  id: string;
  senderId: string;
  receiverId: string;
  serviceRequestId: string;
  content: string;
  messageType: 'text' | 'template' | 'system';
  templateId?: number;
  isRead: boolean;
  createdAt: string;
  updatedAt: string;
  // 新增字段
  status?: 'sending' | 'sent' | 'delivered' | 'read' | 'failed';
  isDeleted?: boolean;
  deletedAt?: string;
  deletedBy?: string;
  replyToMessageId?: string;
  replyToMessage?: Message; // 引用的消息
  isEdited?: boolean;
  editedAt?: string;
  originalContent?: string;
  sender?: {
    id: string;
    name: string;
    email: string;
    profileImage?: string;
  };
  receiver?: {
    id: string;
    name: string;
    email: string;
    profileImage?: string;
  };
}

export interface MessageTemplate {
  id: number;
  name: string;
  content: string;
  category: string;
  isActive: boolean;
}

export interface SendMessageData {
  senderId: string;
  receiverId: string;
  serviceRequestId: string;
  content: string;
  messageType?: 'text' | 'template' | 'system';
  templateId?: number;
  replyToMessageId?: string; // 回复消息ID
}

export class MessageService {
  /**
   * 依案件解析站內訊息的對立方 users.id（工匠↔客戶、公司擁有者↔客戶）。
   * 用於修正 URL 開啟對話時誤將收件人設成自己的情況。
   */
  static async resolveCounterpartyUserIdForServiceRequest(
    serviceRequestId: string,
    currentUserId: string
  ): Promise<string | null> {
    if (!isValidUUID(serviceRequestId) || !isValidUUID(currentUserId)) {
      return null;
    }

    const { data: sr, error } = await supabase
      .from('service_requests')
      .select('user_id, craftsman_id, company_id')
      .eq('id', serviceRequestId)
      .maybeSingle();

    if (error || !sr) {
      console.warn('resolveCounterpartyUserIdForServiceRequest: 無法讀取案件', error);
      return null;
    }

    const customerId = sr.user_id;

    if (customerId === currentUserId) {
      if (sr.craftsman_id) {
        const { data: cm } = await supabase
          .from('craftsmen')
          .select('user_id')
          .eq('id', sr.craftsman_id)
          .maybeSingle();
        if (cm?.user_id && cm.user_id !== currentUserId) return cm.user_id;
      }
      if (sr.company_id) {
        const { data: co } = await supabase
          .from('companies')
          .select('user_id')
          .eq('id', sr.company_id)
          .maybeSingle();
        if (co?.user_id && co.user_id !== currentUserId) return co.user_id;
      }
      return null;
    }

    if (sr.craftsman_id) {
      const { data: cm } = await supabase
        .from('craftsmen')
        .select('user_id')
        .eq('id', sr.craftsman_id)
        .maybeSingle();
      if (
        cm?.user_id === currentUserId &&
        customerId &&
        customerId !== currentUserId
      ) {
        return customerId;
      }
    }

    if (sr.company_id) {
      const { data: co } = await supabase
        .from('companies')
        .select('user_id')
        .eq('id', sr.company_id)
        .maybeSingle();
      if (
        co?.user_id === currentUserId &&
        customerId &&
        customerId !== currentUserId
      ) {
        return customerId;
      }
    }

    if (customerId && customerId !== currentUserId) {
      return customerId;
    }

    return null;
  }

  /**
   * 發送消息
   */
  static async sendMessage(data: SendMessageData): Promise<Message> {
    try {
      console.log('發送消息:', data);

      const {
        data: { user: authUser },
        error: authErr
      } = await supabase.auth.getUser();
      if (authErr || !authUser?.id) {
        throw new Error('發送消息失敗: 請先登入');
      }

      const senderId = authUser.id;
      if (data.senderId !== senderId) {
        console.warn(
          'MessageService.sendMessage: payload senderId 與登入帳號不符，已改用 auth uid',
          { payloadSenderId: data.senderId, authUid: senderId }
        );
      }

      let receiverId = data.receiverId;
      if (!receiverId || receiverId === senderId) {
        const resolved = await this.resolveCounterpartyUserIdForServiceRequest(
          data.serviceRequestId,
          senderId
        );
        if (!resolved || resolved === senderId) {
          throw new Error(
            '發送消息失敗: 無法確認收件人。請重新整理頁面或從案件詳情再次開啟訊息。'
          );
        }
        receiverId = resolved;
      }

      const { data: message, error } = await supabase
        .from('messages')
        .insert({
          sender_id: senderId,
          receiver_id: receiverId,
          service_request_id: data.serviceRequestId,
          content: data.content,
          message_type: data.messageType || 'text',
          template_id: data.templateId || null,
          status: 'sent', // 默认状态为已发送
          reply_to_message_id: data.replyToMessageId || null
        })
        .select(`
          *,
          sender:users!sender_id(id, name, email, profile_image),
          receiver:users!receiver_id(id, name, email, profile_image)
        `)
        .single();

      if (error) {
        console.error('發送消息失敗:', error);
        throw new Error(`發送消息失敗: ${error.message}`);
      }

      console.log('消息發送成功:', message);

      MixpanelAnalytics.messageSent({
        conversation_id: data.serviceRequestId,
        message_type: (data.messageType || 'text') as 'text' | 'template' | 'system',
        channel: 'chat',
        message_length: data.content?.length ?? 0,
      });

      // 創建通知給接收者
      await this.createMessageNotification(receiverId, data.content, data.serviceRequestId);

      return this.mapMessageFromDB(message);
    } catch (error) {
      console.error('MessageService.sendMessage 錯誤:', error);
      throw error;
    }
  }

  /**
   * 獲取對話消息
   */
  static async getConversation(serviceRequestId: string, userId: string): Promise<Message[]> {
    try {

      // 驗證 serviceRequestId 格式
      if (!serviceRequestId || typeof serviceRequestId !== 'string') {
        console.warn('無效的 serviceRequestId:', serviceRequestId);
        return [];
      }

      // 優化：先查消息，再查用戶信息（避免 JOIN 超時）
      const { data: messages, error } = await supabase
        .from('messages')
        .select('*')
        .eq('service_request_id', serviceRequestId)
        .order('created_at', { ascending: true })
        .limit(500); // 限制結果數量，避免超時
      
      // ✅ 處理超時錯誤（包括 504 Gateway Timeout）
      const isTimeoutError = (err: any) => 
        err?.message?.includes('timeout') || 
        err?.message?.includes('canceling statement') ||
        err?.message?.includes('upstream request timeout') ||
        err?.message?.includes('Gateway Timeout') ||
        err?.code === 'PGRST116' ||
        err?.code === '57014' ||
        err?.code === '504' ||
        String(err?.code) === '504';
      
      if (error) {
        // ✅ 如果是超時、502、504 或網絡錯誤，返回空數組而不是拋出錯誤
        if (isTimeoutError(error) || 
            error.message?.includes('502') || 
            error.message?.includes('504') ||
            error.message?.includes('Gateway Timeout') ||
            error.message?.includes('Failed to fetch') || 
            error.message?.includes('CORS')) {
          console.warn('網絡錯誤或超時（504/502），返回空消息列表:', error.message || error.code);
          return [];
        }
        console.error('獲取對話消息失敗:', error);
        throw new Error(`獲取對話消息失敗: ${error.message}`);
      }
      
      // 如果沒有消息，返回空數組
      if (!messages || messages.length === 0) {
        return [];
      }
      
      // ✅ 如果查詢成功，獲取用戶信息（添加錯誤處理）
      const userIds = new Set<string>();
      messages.forEach(msg => {
        if (msg.sender_id) userIds.add(msg.sender_id);
        if (msg.receiver_id) userIds.add(msg.receiver_id);
      });
      
      let usersMap = new Map();
      if (userIds.size > 0) {
        try {
          const { data: usersData, error: usersError } = await supabase
            .from('users')
            .select('id, name, email, profile_image')
            .in('id', Array.from(userIds));
          
          // ✅ 處理用戶查詢超時
          if (usersError) {
            const isUsersTimeout = usersError.message?.includes('timeout') || 
                                   usersError.message?.includes('504') ||
                                   usersError.message?.includes('Gateway Timeout') ||
                                   usersError.code === '504';
            
            if (isUsersTimeout) {
              console.warn('獲取用戶信息超時，使用空映射:', usersError.message);
            } else {
              console.error('獲取用戶信息失敗:', usersError);
            }
          } else {
            usersMap = new Map(usersData?.map(u => [u.id, u]) || []);
          }
        } catch (usersFetchError: any) {
          console.warn('獲取用戶信息時發生錯誤，使用空映射:', usersFetchError?.message);
        }
      }
      
      // 將用戶信息附加到消息中
      return messages.map(msg => {
        const mappedMsg = this.mapMessageFromDB(msg);
        mappedMsg.sender = usersMap.get(msg.sender_id);
        mappedMsg.receiver = usersMap.get(msg.receiver_id);
        return mappedMsg;
      });
    } catch (error: any) {
      console.error('MessageService.getConversation 錯誤:', error);
      
      // ✅ 處理超時錯誤（包括 504 Gateway Timeout）
      const isTimeoutError = (err: any) => 
        err?.message?.includes('timeout') || 
        err?.message?.includes('canceling statement') ||
        err?.message?.includes('upstream request timeout') ||
        err?.message?.includes('Gateway Timeout') ||
        err?.code === 'PGRST116' ||
        err?.code === '57014' ||
        err?.code === '504' ||
        String(err?.code) === '57014' ||
        String(err?.code) === '504';
      
      // ✅ 如果是超時、502、504 或網絡錯誤，返回空數組
      if (isTimeoutError(error) || 
          error?.message?.includes('502') || 
          error?.message?.includes('504') ||
          error?.message?.includes('Gateway Timeout') ||
          error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('CORS') ||
          error?.message?.includes('500')) {
        console.warn('查詢超時或網絡錯誤（504/502），返回空消息列表（允許用戶開始新對話）');
        return [];
      }
      
      throw error;
    }
  }

  /**
   * 獲取用戶的所有消息（按服務請求分組）
   */
  static async getUserMessages(userId: string): Promise<{ [serviceRequestId: string]: Message[] }> {
    try {
      console.log('獲取用戶消息:', userId);

      // 部分舊帳號可能非 UUID，仍允許查詢
      if (!userId) {
        console.log('缺少 userId，無法查詢消息');
        return {};
      }

      const { data: messages, error } = await supabase
        .from('messages')
        .select(`
          *,
          sender:users!sender_id(id, name, email, profile_image),
          receiver:users!receiver_id(id, name, email, profile_image)
        `)
        .or(`sender_id.eq.${userId},receiver_id.eq.${userId}`)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('獲取用戶消息失敗:', error);
        throw new Error(`獲取用戶消息失敗: ${error.message}`);
      }

      // 按服務請求 ID 分組
      const groupedMessages: { [serviceRequestId: string]: Message[] } = {};
      messages?.forEach(message => {
        const mapped = this.mapMessageFromDB(message);
        if (!groupedMessages[mapped.serviceRequestId]) {
          groupedMessages[mapped.serviceRequestId] = [];
        }
        groupedMessages[mapped.serviceRequestId].push(mapped);
      });

      // 每組內按時間排序
      Object.keys(groupedMessages).forEach(key => {
        groupedMessages[key].sort((a, b) => 
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );
      });

      console.log(`獲取到 ${Object.keys(groupedMessages).length} 個對話`);

      return groupedMessages;
    } catch (error) {
      console.error('MessageService.getUserMessages 錯誤:', error);
      throw error;
    }
  }

  /**
   * ✅ 標記消息為已讀（優化：處理空數組和超時錯誤）
   */
  static async markAsRead(messageIds: string[]): Promise<void> {
    try {
      // ✅ 如果沒有消息ID，直接返回，不發送請求
      if (!messageIds || messageIds.length === 0) {
        console.log('沒有需要標記為已讀的消息');
        return;
      }

      const { error } = await supabase
        .from('messages')
        .update({ 
          is_read: true, 
          updated_at: new Date().toISOString() 
        })
        .in('id', messageIds);

      if (error) {
        // ✅ 處理超時錯誤（504）
        const isTimeoutError = error.message?.includes('timeout') || 
                              error.message?.includes('504') ||
                              error.message?.includes('Gateway Timeout') ||
                              error.message?.includes('upstream request timeout') ||
                              error.code === '504';
        
        if (isTimeoutError) {
          console.warn('標記已讀超時（504），但不影響使用:', error.message);
          return; // 超時時靜默返回，不拋出錯誤
        }
        
        console.error('標記消息為已讀失敗:', error);
        throw new Error(`標記消息為已讀失敗: ${error.message}`);
      }

      // Messages marked as read successfully
    } catch (error: any) {
      console.error('MessageService.markAsRead 錯誤:', error);
      
      // ✅ 如果是超時錯誤，靜默處理
      if (error?.message?.includes('timeout') || 
          error?.message?.includes('504') ||
          error?.message?.includes('Gateway Timeout')) {
        console.warn('標記已讀超時，靜默處理');
        return;
      }
      
      throw error;
    }
  }

  /**
   * 標記對話為已讀
   */
  static async markConversationAsRead(serviceRequestId: string, userId: string): Promise<void> {
    try {
      console.log('標記對話為已讀:', { serviceRequestId, userId });

      // 如果 userId 不是有效的 UUID（例如 temp-user-id），跳過查詢
      if (!isValidUUID(userId)) {
        console.log('用戶ID不是有效的UUID，跳過標記已讀');
        return;
      }

      const { error } = await supabase
        .from('messages')
        .update({ 
          is_read: true, 
          updated_at: new Date().toISOString() 
        })
        .eq('service_request_id', serviceRequestId)
        .eq('receiver_id', userId)
        .eq('is_read', false);

      if (error) {
        console.error('標記對話為已讀失敗:', error);
        throw new Error(`標記對話為已讀失敗: ${error.message}`);
      }

      console.log('對話已標記為已讀');
    } catch (error) {
      console.error('MessageService.markConversationAsRead 錯誤:', error);
      throw error;
    }
  }

  /**
   * 獲取未讀消息數量
   */
  static async getUnreadCount(userId: string): Promise<number> {
    try {
      console.log('獲取未讀消息數量:', userId);

      // 如果 userId 不是有效的 UUID（例如 temp-user-id），跳過查詢
      if (!isValidUUID(userId)) {
        console.log('用戶ID不是有效的UUID，跳過查詢');
        return 0;
      }

      const { count, error } = await supabase
        .from('messages')
        .select('*', { count: 'exact', head: true })
        .eq('receiver_id', userId)
        .eq('is_read', false);

      if (error) {
        // ✅ 處理 504 超時錯誤
        const isTimeoutError = error.message?.includes('timeout') || 
                              error.message?.includes('504') ||
                              error.message?.includes('Gateway Timeout') ||
                              error.message?.includes('upstream request timeout') ||
                              error.code === '504' ||
                              error.code === '57014';
        
        if (isTimeoutError) {
          console.warn('獲取未讀消息數量超時（504），返回 0:', error.message);
          return 0; // 超時時返回 0，不拋出錯誤
        }
        
        console.error('獲取未讀消息數量失敗:', error);
        throw new Error(`獲取未讀消息數量失敗: ${error.message}`);
      }

      const unreadCount = count || 0;
      console.log(`未讀消息數量: ${unreadCount}`);

      return unreadCount;
    } catch (error: any) {
      console.error('MessageService.getUnreadCount 錯誤:', error);
      
      // ✅ 如果是超時錯誤，返回 0
      if (error?.message?.includes('timeout') || 
          error?.message?.includes('504') ||
          error?.message?.includes('Gateway Timeout')) {
        console.warn('獲取未讀消息數量超時，返回 0');
        return 0;
      }
      
      return 0;
    }
  }

  /**
   * 獲取消息模板
   */
  static async getMessageTemplates(): Promise<MessageTemplate[]> {
    try {
      console.log('獲取消息模板');

      const { data: templates, error } = await supabase
        .from('message_templates')
        .select('*')
        .eq('is_active', true)
        .order('category', { ascending: true });

      if (error) {
        // ✅ 處理 504 超時錯誤
        const isTimeoutError = error.message?.includes('timeout') || 
                              error.message?.includes('504') ||
                              error.message?.includes('Gateway Timeout') ||
                              error.message?.includes('upstream request timeout') ||
                              error.code === '504';
        
        if (isTimeoutError) {
          console.warn('獲取消息模板超時（504），返回空數組:', error.message);
          return []; // 超時時返回空數組，不拋出錯誤
        }
        
        console.error('獲取消息模板失敗:', error);
        throw new Error(`獲取消息模板失敗: ${error.message}`);
      }

      console.log(`獲取到 ${templates?.length || 0} 個模板`);

      return templates?.map(template => ({
        id: template.id,
        name: template.name,
        content: template.content,
        category: template.category,
        isActive: template.is_active
      })) || [];
    } catch (error: any) {
      console.error('MessageService.getMessageTemplates 錯誤:', error);
      
      // ✅ 如果是超時錯誤，返回空數組
      if (error?.message?.includes('timeout') || 
          error?.message?.includes('504') ||
          error?.message?.includes('Gateway Timeout')) {
        console.warn('獲取消息模板超時，返回空數組');
        return [];
      }
      
      return [];
    }
  }

  /**
   * 創建消息通知
   */
  private static async createMessageNotification(
    userId: string, 
    messageContent: string, 
    serviceRequestId: string
  ): Promise<void> {
    try {
      // 使用靜態導入的 NotificationService
      await NotificationService.createNotification(
        userId,
        'message',
        '收到新消息',
        messageContent.length > 50 ? 
          messageContent.substring(0, 50) + '...' : 
          messageContent,
        serviceRequestId
      );
    } catch (error) {
      console.error('創建消息通知失敗:', error);
      // 不拋出錯誤，避免影響消息發送
    }
  }

  /**
   * 映射數據庫消息到前端格式
   */
  private static mapMessageFromDB(dbMessage: any): Message {
    return {
      id: dbMessage.id,
      senderId: dbMessage.sender_id,
      receiverId: dbMessage.receiver_id,
      serviceRequestId: dbMessage.service_request_id,
      content: dbMessage.content,
      messageType: dbMessage.message_type,
      templateId: dbMessage.template_id,
      isRead: dbMessage.is_read,
      createdAt: dbMessage.created_at,
      updatedAt: dbMessage.updated_at,
      // 新增字段映射
      status: dbMessage.status || 'sent',
      isDeleted: dbMessage.is_deleted || false,
      deletedAt: dbMessage.deleted_at,
      deletedBy: dbMessage.deleted_by,
      replyToMessageId: dbMessage.reply_to_message_id,
      isEdited: dbMessage.is_edited || false,
      editedAt: dbMessage.edited_at,
      originalContent: dbMessage.original_content,
      sender: dbMessage.sender ? {
        id: dbMessage.sender.id,
        name: dbMessage.sender.name,
        email: dbMessage.sender.email,
        profileImage: dbMessage.sender.profile_image
      } : undefined,
      receiver: dbMessage.receiver ? {
        id: dbMessage.receiver.id,
        name: dbMessage.receiver.name,
        email: dbMessage.receiver.email,
        profileImage: dbMessage.receiver.profile_image
      } : undefined
    };
  }

  /**
   * 撤回消息（2分钟内可撤回）
   */
  static async deleteMessage(messageId: string, userId: string): Promise<boolean> {
    try {
      // 检查消息是否存在且是发送者
      const { data: message, error: fetchError } = await supabase
        .from('messages')
        .select('sender_id, created_at')
        .eq('id', messageId)
        .single();

      if (fetchError || !message) {
        throw new Error('消息不存在');
      }

      if (message.sender_id !== userId) {
        throw new Error('只能撤回自己发送的消息');
      }

      // 检查是否在2分钟内
      const messageTime = new Date(message.created_at).getTime();
      const now = Date.now();
      const twoMinutes = 2 * 60 * 1000;

      if (now - messageTime > twoMinutes) {
        throw new Error('消息发送超过2分钟，无法撤回');
      }

      // 标记为已删除
      const { error } = await supabase
        .from('messages')
        .update({
          is_deleted: true,
          deleted_at: new Date().toISOString(),
          deleted_by: userId,
          content: '[消息已撤回]'
        })
        .eq('id', messageId);

      if (error) {
        throw new Error(`撤回消息失败: ${error.message}`);
      }

      return true;
    } catch (error: any) {
      console.error('撤回消息失败:', error);
      throw error;
    }
  }

  /**
   * 编辑消息
   */
  static async editMessage(messageId: string, userId: string, newContent: string): Promise<boolean> {
    try {
      // 检查消息是否存在且是发送者
      const { data: message, error: fetchError } = await supabase
        .from('messages')
        .select('sender_id, content, is_deleted')
        .eq('id', messageId)
        .single();

      if (fetchError || !message) {
        throw new Error('消息不存在');
      }

      if (message.sender_id !== userId) {
        throw new Error('只能编辑自己发送的消息');
      }

      if (message.is_deleted) {
        throw new Error('已撤回的消息无法编辑');
      }

      // 更新消息
      const { error } = await supabase
        .from('messages')
        .update({
          content: newContent,
          is_edited: true,
          edited_at: new Date().toISOString(),
          original_content: message.content,
          updated_at: new Date().toISOString()
        })
        .eq('id', messageId);

      if (error) {
        throw new Error(`编辑消息失败: ${error.message}`);
      }

      return true;
    } catch (error: any) {
      console.error('编辑消息失败:', error);
      throw error;
    }
  }

  /**
   * 更新消息状态
   */
  static async updateMessageStatus(messageId: string, status: 'sending' | 'sent' | 'delivered' | 'read' | 'failed'): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('messages')
        .update({
          status: status,
          updated_at: new Date().toISOString()
        })
        .eq('id', messageId);

      if (error) {
        throw new Error(`更新消息状态失败: ${error.message}`);
      }

      return true;
    } catch (error: any) {
      console.error('更新消息状态失败:', error);
      throw error;
    }
  }

  /**
   * 檢查用戶是否為 Premium 會員（用於優先級處理）
   */
  static async isPremiumUser(userId: string): Promise<boolean> {
    try {
      // 檢查公司是否為 Premium
      const { data: company } = await supabase
        .from('companies')
        .select('is_premium, premium_until')
        .eq('user_id', userId)
        .single();

      if (company) {
        return company.is_premium && 
               company.premium_until && 
               new Date(company.premium_until) > new Date();
      }

      // 檢查工匠是否為 Premium
      const { data: craftsman } = await supabase
        .from('craftsmen')
        .select('is_premium, premium_until')
        .eq('user_id', userId)
        .single();

      if (craftsman) {
        return craftsman.is_premium && 
               craftsman.premium_until && 
               new Date(craftsman.premium_until) > new Date();
      }

      return false;
    } catch (error) {
      console.error('檢查 Premium 狀態失敗:', error);
      return false;
    }
  }

  /**
   * 獲取 Premium 用戶的消息（優先級排序）
   */
  static async getPremiumMessages(userId: string): Promise<Message[]> {
    try {
      const isPremium = await this.isPremiumUser(userId);
      if (!isPremium) {
        return [];
      }

      // 獲取所有消息，Premium 消息會自動標記
      const allMessages = await this.getUserMessages(userId);
      const premiumMessages: Message[] = [];

      // 提取所有 Premium 相關消息
      Object.values(allMessages).forEach(messages => {
        messages.forEach(msg => {
          // 如果消息內容包含 Premium 標記，或發送者是 Premium 用戶
          if (msg.content.includes('[Premium') || 
              msg.content.includes('Premium 专属')) {
            premiumMessages.push(msg);
          }
        });
      });

      return premiumMessages.sort((a, b) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    } catch (error) {
      console.error('獲取 Premium 消息失敗:', error);
      return [];
    }
  }
}


